import { useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    createViewFiles,
    createViewFilesTBGL,
    createViewFilesFinJournals,
    createViewFilesArchive,
} from "@/ui/views";
import { computeIsViewingAsAdmin } from "@/store/auth/sliceAuth";
import { useOpenFileDetail } from "@/ui/router";
import { mapApiFileToStore } from "@/utils/mapApiFileToStore";
import { wrapApiError } from "@/utils/wrapApiError";
import { NOOP } from "@/utils/utils";
import { filesFiltersToQuery } from "@/ui/organisms";
import type { TServerTableQueryInput } from "@/useCases/useServerTable";
import type { TFilesFilterQuery } from "@/ui/organisms";
import type { TCompositionDeps } from "./types";

export const wireFiles = ({ store, useAppStore, api }: TCompositionDeps) => {
    /**
     * Shared facet state factory — each variant gets its own state.
     */
    type TFacetState = {
        businessPartnerOptions: { value: string; label: string }[];
        submissionOptions: { value: string; label: string; businessPartnerId?: string }[];
    };
    const EMPTY_FACETS: TFacetState = { businessPartnerOptions: [], submissionOptions: [] };

    const ViewFiles = createViewFiles({
        useCases: () => {
            const navigate = useNavigate();
            const openFileDetail = useOpenFileDetail();
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const filesState = useAppStore((state) => state.filesListState);
            const canAccessAllFiles = useAppStore(
                (state) => state.user?.permissions?.includes("files_all_page_access") ?? false,
            );
            const canFilterAllFiles = useAppStore(
                (state) => state.user?.permissions?.includes("files_all_filter") ?? false,
            );
            // `files_all_open` is the review-side open grant. Supporting-files holders
            // (Business Partners) open the same page in restricted mode — their tab is
            // the only surface they have for it — so either grant opens a row. This
            // permission has no backend counterpart; the page's own gates do the real
            // enforcement.
            const canOpenAllFiles = useAppStore(
                (state) =>
                    (state.user?.permissions?.includes("files_all_open") ?? false) ||
                    (state.user?.permissions?.includes("supporting_files_view") ?? false),
            );
            const [deals, setDeals] = useState<{
                data: Array<{ id: string; name: string; code: string }> | null;
                loading: boolean;
            }>({ data: null, loading: true });
            const [facets, setFacets] = useState<TFacetState>(EMPTY_FACETS);

            // Track the last server-table query so "Try Again" (onRefresh) can
            // replay it instead of resetting to an unfiltered page 1.
            const lastQueryRef = useRef<TFilesFilterQuery>(
                filesFiltersToQuery({ columnFilters: [], sorting: [], page: 1, pageSize: 50 }),
            );

            const runQuery = useCallback(
                (query: TFilesFilterQuery, signal?: AbortSignal) => {
                    lastQueryRef.current = query;
                    store.getState().setFilesLoading();
                    api.files
                        .list(query, signal)
                        .then((response) => {
                            store.getState().setFilesList({
                                items: response.items.map(mapApiFileToStore),
                                total: response.total,
                                totalUnfiltered: response.total_unfiltered ?? response.total,
                                page: response.page,
                                pageSize: response.page_size,
                            });
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            store
                                .getState()
                                .setFilesError(wrapApiError(error, "Failed to load files"));
                        });
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            const loadFacets = useCallback(() => {
                api.files
                    .filterOptions()
                    .then((response) => {
                        setFacets({
                            businessPartnerOptions: response.businessPartners.map((bp) => ({
                                value: bp.id,
                                label: bp.name,
                            })),
                            submissionOptions: response.submissions.map((s) => ({
                                value: s.id,
                                label: s.name,
                                businessPartnerId: s.businessPartnerId ?? undefined,
                            })),
                        });
                    })
                    .catch(() => setFacets(EMPTY_FACETS));
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            const loadDeals = useCallback(() => {
                setDeals({ data: null, loading: true });
                api.deals
                    .list({ active_only: true, limit: 100 })
                    .then((response) => {
                        setDeals({
                            data: response.items.map((d) => ({
                                id: d.id,
                                name: d.name,
                                code: d.code || "",
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setDeals({ data: [], loading: false });
                    });
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            const loadAll = useCallback(() => {
                loadFacets();
                loadDeals();
            }, [loadFacets, loadDeals]);

            return {
                onMount: loadAll,
                onUnmount: NOOP,
                useFilesState: () => filesState,
                useDeals: () => deals,
                onFileClick: useCallback(
                    (file: { id: string }) => {
                        openFileDetail(file.id);
                    },
                    [openFileDetail],
                ),
                onUploadFile: useCallback(
                    async (data: {
                        dealId: string;
                        period: string;
                        fileType: string;
                        file: File;
                        sheetName?: string;
                        tbSheetName?: string;
                        glSheetName?: string;
                    }) => {
                        await api.files.upload(data.file, {
                            deal_id: data.dealId,
                            period: data.period,
                            file_type: data.fileType as
                                | "trial_balance"
                                | "general_ledger"
                                | "open_trial_balance"
                                | "financial_journals"
                                | "tb_gl",
                            sheet_name: data.sheetName,
                            tb_sheet_name: data.tbSheetName,
                            gl_sheet_name: data.glSheetName,
                        });
                    },
                    [],
                ),
                onQuery: useCallback(
                    (input: TServerTableQueryInput, signal?: AbortSignal) =>
                        runQuery(filesFiltersToQuery(input), signal),
                    [runQuery],
                ),
                onRefresh: useCallback(() => runQuery(lastQueryRef.current), [runQuery]),
                useFacets: () => facets,
                onAccessDenied: useCallback(
                    // "/" resolves the landing page from this user's
                    // permissions and their company's Homepage v2 flag.
                    () => navigate("/", { replace: true }),
                    [navigate],
                ),
                useCanAccess: () => canAccessAllFiles,
                useCanFilter: () => canFilterAllFiles,
                useCanOpenFile: () => canOpenAllFiles,
            };
        },
    });

    const ViewFilesTBGL = createViewFilesTBGL({
        useCases: () => {
            const navigate = useNavigate();
            const openFileDetail = useOpenFileDetail();
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const filesState = useAppStore((state) => state.filesListState);
            const canAccessTBGL = useAppStore(
                (state) => state.user?.permissions?.includes("tbgl_files_page_access") ?? false,
            );
            const [facets, setFacets] = useState<TFacetState>(EMPTY_FACETS);

            // Track the last server-table query so "Try Again" (onRefresh) can
            // replay it instead of resetting to an unfiltered page 1.
            const lastQueryRef = useRef<TFilesFilterQuery>(
                filesFiltersToQuery(
                    { columnFilters: [], sorting: [], page: 1, pageSize: 50 },
                    { exclude_financial_journals: true },
                ),
            );

            const runQuery = useCallback(
                (query: TFilesFilterQuery, signal?: AbortSignal) => {
                    lastQueryRef.current = query;
                    store.getState().setFilesLoading();
                    api.files
                        .list(query, signal)
                        .then((response) => {
                            store.getState().setFilesList({
                                items: response.items.map(mapApiFileToStore),
                                total: response.total,
                                totalUnfiltered: response.total_unfiltered ?? response.total,
                                page: response.page,
                                pageSize: response.page_size,
                            });
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            store
                                .getState()
                                .setFilesError(wrapApiError(error, "Failed to load files"));
                        });
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            const loadFacets = useCallback(() => {
                api.files
                    .filterOptions({ exclude_financial_journals: true })
                    .then((response) => {
                        setFacets({
                            businessPartnerOptions: response.businessPartners.map((bp) => ({
                                value: bp.id,
                                label: bp.name,
                            })),
                            submissionOptions: response.submissions.map((s) => ({
                                value: s.id,
                                label: s.name,
                                businessPartnerId: s.businessPartnerId ?? undefined,
                            })),
                        });
                    })
                    .catch(() => setFacets(EMPTY_FACETS));
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            return {
                onMount: loadFacets,
                onUnmount: NOOP,
                useFilesState: () => filesState,
                onFileClick: useCallback(
                    (file: { id: string }) => {
                        openFileDetail(file.id);
                    },
                    [openFileDetail],
                ),
                onQuery: useCallback(
                    (input: TServerTableQueryInput, signal?: AbortSignal) =>
                        runQuery(
                            filesFiltersToQuery(input, { exclude_financial_journals: true }),
                            signal,
                        ),
                    [runQuery],
                ),
                onRefresh: useCallback(() => runQuery(lastQueryRef.current), [runQuery]),
                useFacets: () => facets,
                onAccessDenied: useCallback(
                    // "/" resolves the landing page from this user's
                    // permissions and their company's Homepage v2 flag.
                    () => navigate("/", { replace: true }),
                    [navigate],
                ),
                useCanAccess: () => canAccessTBGL,
            };
        },
    });

    const ViewFilesFinJournals = createViewFilesFinJournals({
        useCases: () => {
            const navigate = useNavigate();
            const openFileDetail = useOpenFileDetail();
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const filesState = useAppStore((state) => state.filesListState);
            const canViewFinJournals = useAppStore(
                (state) => state.user?.permissions?.includes("finjournal_files_view") ?? false,
            );
            const [facets, setFacets] = useState<TFacetState>(EMPTY_FACETS);

            // Track the last server-table query so "Try Again" (onRefresh) can
            // replay it instead of resetting to an unfiltered page 1.
            const lastQueryRef = useRef<TFilesFilterQuery>(
                filesFiltersToQuery(
                    { columnFilters: [], sorting: [], page: 1, pageSize: 50 },
                    { only_financial_journals: true },
                ),
            );

            const runQuery = useCallback(
                (query: TFilesFilterQuery, signal?: AbortSignal) => {
                    lastQueryRef.current = query;
                    store.getState().setFilesLoading();
                    api.files
                        .list(query, signal)
                        .then((response) => {
                            store.getState().setFilesList({
                                items: response.items.map(mapApiFileToStore),
                                total: response.total,
                                totalUnfiltered: response.total_unfiltered ?? response.total,
                                page: response.page,
                                pageSize: response.page_size,
                            });
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            store
                                .getState()
                                .setFilesError(wrapApiError(error, "Failed to load files"));
                        });
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            const loadFacets = useCallback(() => {
                api.files
                    .filterOptions({ only_financial_journals: true })
                    .then((response) => {
                        setFacets({
                            businessPartnerOptions: response.businessPartners.map((bp) => ({
                                value: bp.id,
                                label: bp.name,
                            })),
                            submissionOptions: response.submissions.map((s) => ({
                                value: s.id,
                                label: s.name,
                                businessPartnerId: s.businessPartnerId ?? undefined,
                            })),
                        });
                    })
                    .catch(() => setFacets(EMPTY_FACETS));
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            return {
                onMount: loadFacets,
                onUnmount: NOOP,
                useFilesState: () => filesState,
                onFileClick: useCallback(
                    (file: { id: string }) => {
                        openFileDetail(file.id);
                    },
                    [openFileDetail],
                ),
                onQuery: useCallback(
                    (input: TServerTableQueryInput, signal?: AbortSignal) =>
                        runQuery(
                            filesFiltersToQuery(input, { only_financial_journals: true }),
                            signal,
                        ),
                    [runQuery],
                ),
                onRefresh: useCallback(() => runQuery(lastQueryRef.current), [runQuery]),
                useFacets: () => facets,
                onAccessDenied: useCallback(
                    // "/" resolves the landing page from this user's
                    // permissions and their company's Homepage v2 flag.
                    () => navigate("/", { replace: true }),
                    [navigate],
                ),
                useCanAccess: () => canViewFinJournals,
            };
        },
    });

    const PAGE_SIZE = 50;

    const ViewFilesArchive = createViewFilesArchive({
        useCases: () => {
            const navigate = useNavigate();
            const openFileDetail = useOpenFileDetail();
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const effectiveSubgroup = useAppStore((state) => state.effectiveSubgroup);
            const storeUser = useAppStore((state) => state.user);
            const archivedFilesState = useAppStore((state) => state.archivedFilesListState);
            const isViewingAsAdmin = computeIsViewingAsAdmin(storeUser, effectiveSubgroup);

            // Track the last server-table query so "Try Again" (onRefresh) can
            // replay it instead of resetting to an unfiltered page 1.
            const lastQueryRef = useRef<TFilesFilterQuery>(
                filesFiltersToQuery({
                    columnFilters: [],
                    sorting: [],
                    page: 1,
                    pageSize: PAGE_SIZE,
                }),
            );

            // Server-side: the archive lists archived files only, scoped by the
            // current server-table query plus a forced archived=true filter.
            const runQuery = useCallback(
                (query: TFilesFilterQuery, signal?: AbortSignal) => {
                    lastQueryRef.current = query;
                    store.getState().setArchivedFilesLoading();
                    api.files
                        .list({ ...query, archived: true }, signal)
                        .then((response) => {
                            store.getState().setArchivedFilesList({
                                items: response.items.map(mapApiFileToStore),
                                total: response.total,
                                totalUnfiltered: response.total_unfiltered ?? response.total,
                                page: response.page,
                                pageSize: response.page_size,
                            });
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            store
                                .getState()
                                .setArchivedFilesError(
                                    wrapApiError(error, "Failed to load archived files"),
                                );
                        });
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            return {
                onMount: NOOP,
                onUnmount: NOOP,
                useFilesState: () => archivedFilesState,
                onFileClick: useCallback(
                    (file: { id: string }) => {
                        openFileDetail(file.id);
                    },
                    [openFileDetail],
                ),
                onQuery: useCallback(
                    (input: TServerTableQueryInput, signal?: AbortSignal) =>
                        runQuery(filesFiltersToQuery(input), signal),
                    [runQuery],
                ),
                onRefresh: useCallback(() => runQuery(lastQueryRef.current), [runQuery]),
                useFacets: () => EMPTY_FACETS,
                onAccessDenied: useCallback(
                    // "/" resolves the landing page from this user's
                    // permissions and their company's Homepage v2 flag.
                    () => navigate("/", { replace: true }),
                    [navigate],
                ),
                useCanAccess: () => isViewingAsAdmin,
            };
        },
    });

    return {
        ViewFiles,
        ViewFilesTBGL,
        ViewFilesFinJournals,
        ViewFilesArchive,
    };
};
